import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { getProjects, getUploads, createProject, createUpload } from '../../services/api'
import { Project, Upload } from '../../types'
import { Plus, Upload as UploadIcon, Map, ChevronRight, FolderOpen, AlertCircle, Loader } from 'lucide-react'

export default function ProjectList() {
  const { projectId } = useParams()
  const navigate = useNavigate()
  const [projects, setProjects] = useState<Project[]>([])
  const [uploads, setUploads] = useState<Upload[]>([])
  const [selectedProject, setSelectedProject] = useState<number | null>(projectId ? parseInt(projectId) : null)
  const [showNewProject, setShowNewProject] = useState(false)
  const [showUpload, setShowUpload] = useState(false)
  const [uploadProgress, setUploadProgress] = useState(0)
  const [uploadError, setUploadError] = useState('')
  const [isUploading, setIsUploading] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    getProjects()
      .then(res => {
        setProjects(res.data.projects)
        if (projectId) {
          setSelectedProject(parseInt(projectId))
        }
      })
      .catch(console.error)
      .finally(() => setLoading(false))
  }, [projectId])

  useEffect(() => {
    if (selectedProject) {
      getUploads(selectedProject)
        .then(res => setUploads(res.data.uploads))
        .catch(console.error)
    }
  }, [selectedProject])

  const handleCreateProject = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    const form = e.currentTarget
    const data = new FormData(form)

    try {
      const res = await createProject({
        client_id: parseInt(data.get('client_id') as string),
        name: data.get('name') as string,
        description: data.get('description') as string,
      })
      setProjects([...projects, { ...res.data, description: data.get('description') as string }])
      setShowNewProject(false)
      form.reset()
    } catch (err) {
      alert('Failed to create project')
    }
  }

  const handleUpload = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    if (!selectedProject) return

    setIsUploading(true)
    setUploadProgress(0)
    setUploadError('')

    const form = e.currentTarget
    const formData = new FormData(form)
    formData.append('project_id', selectedProject.toString())

    // Get selected indices
    const checkboxes = form.querySelectorAll<HTMLInputElement>('input[name="indices"]:checked')
    const indices = Array.from(checkboxes).map(cb => cb.value)
    formData.append('selected_indices', JSON.stringify(indices))

    const retainRaw = form.querySelector<HTMLInputElement>('input[name="retain_raw"]')?.checked || false
    formData.append('retain_raw_indices', retainRaw.toString())

    try {
      await createUpload(formData, (percent) => {
        setUploadProgress(percent)
      })

      setShowUpload(false)
      setUploadProgress(0)
      form.reset()

      // Refresh uploads
      const res = await getUploads(selectedProject)
      setUploads(res.data.uploads)
    } catch (err: any) {
      const msg = err.response?.data?.error 
        || err.response?.data?.errors?.map((e: any) => e.msg).join(', ')
        || err.message 
        || 'Upload failed'
      setUploadError(msg)
    } finally {
      setIsUploading(false)
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-navy-800">Projects</h2>
        <button
          onClick={() => setShowNewProject(true)}
          className="btn-primary flex items-center gap-2"
        >
          <Plus size={18} />
          New Project
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Project List */}
        <div className="lg:col-span-1 space-y-3">
          {projects.map(project => (
            <button
              key={project.id}
              onClick={() => {
                setSelectedProject(project.id)
                navigate(`/projects/${project.id}`)
              }}
              className={`w-full text-left p-4 rounded-lg border transition-all ${
                selectedProject === project.id
                  ? 'border-orange-500 bg-orange-50 shadow-sm'
                  : 'border-gray-100 bg-white hover:border-gray-200'
              }`}
            >
              <div className="flex items-center">
                <FolderOpen size={18} className={`mr-3 ${selectedProject === project.id ? 'text-orange-500' : 'text-navy-400'}`} />
                <div>
                  <p className={`font-medium ${selectedProject === project.id ? 'text-orange-700' : 'text-navy-800'}`}>
                    {project.name}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {project.description?.slice(0, 50) || 'No description'}
                  </p>
                </div>
              </div>
            </button>
          ))}
        </div>

        {/* Uploads for selected project */}
        <div className="lg:col-span-3">
          {selectedProject ? (
            <div className="card">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-semibold text-navy-800">
                    {projects.find(p => p.id === selectedProject)?.name}
                  </h3>
                  <p className="text-sm text-gray-500">{uploads.length} uploads</p>
                </div>
                <button
                  onClick={() => setShowUpload(true)}
                  className="btn-primary flex items-center gap-2"
                >
                  <UploadIcon size={18} />
                  Upload ZIP
                </button>
              </div>

              {uploads.length === 0 ? (
                <div className="text-center py-12 text-gray-400">
                  <UploadIcon size={48} className="mx-auto mb-4 opacity-50" />
                  <p>No uploads for this project yet.</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {uploads.map(upload => (
                    <div
                      key={upload.id}
                      className="flex items-center p-4 rounded-lg border border-gray-100 hover:border-orange-200 hover:shadow-sm transition-all"
                    >
                      <div className="flex-1">
                        <p className="font-medium text-navy-800">
                          {upload.original_filename || `Upload #${upload.id}`}
                        </p>
                        <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            upload.status === 'completed' ? 'bg-green-50 text-green-600' :
                            upload.status === 'processing' ? 'bg-blue-50 text-blue-600' :
                            upload.status === 'failed' ? 'bg-red-50 text-red-600' :
                            'bg-yellow-50 text-yellow-600'
                          }`}>
                            {upload.status}
                          </span>
                          <span>{new Date(upload.created_at).toLocaleDateString()}</span>
                          {upload.selected_indices?.length > 0 && (
                            <span className="text-orange-500">
                              {upload.selected_indices.join(', ')}
                              {upload.retain_raw_indices && ' + raw'}
                            </span>
                          )}
                        </div>
                      </div>
                      {upload.status === 'completed' && (
                        <button
                          onClick={() => navigate(`/map/${upload.id}`)}
                          className="ml-4 p-2 text-orange-500 hover:bg-orange-50 rounded-lg transition-colors"
                        >
                          <Map size={20} />
                        </button>
                      )}
                      <ChevronRight size={18} className="text-gray-300 ml-2" />
                    </div>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <div className="card text-center py-16 text-gray-400">
              <FolderOpen size={48} className="mx-auto mb-4 opacity-50" />
              <p>Select a project to view uploads</p>
            </div>
          )}
        </div>
      </div>

      {/* New Project Modal */}
      {showNewProject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-navy-800 mb-4">Create New Project</h3>
            <form onSubmit={handleCreateProject} className="space-y-4">
              <div>
                <label className="label">Client ID</label>
                <input name="client_id" type="number" className="input" required />
              </div>
              <div>
                <label className="label">Project Name</label>
                <input name="name" type="text" className="input" required />
              </div>
              <div>
                <label className="label">Description</label>
                <textarea name="description" className="input" rows={3} />
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowNewProject(false)} className="btn-outline flex-1">
                  Cancel
                </button>
                <button type="submit" className="btn-primary flex-1">
                  Create
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Upload Modal */}
      {showUpload && selectedProject && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-lg font-semibold text-navy-800 mb-4">Upload WebODM Backup</h3>
            <form onSubmit={handleUpload} className="space-y-4">
              {uploadError && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm flex items-start gap-2">
                  <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium">Upload Failed</p>
                    <p>{uploadError}</p>
                  </div>
                </div>
              )}

              <div>
                <label className="label">ZIP File (WebODM Backup)</label>
                <input
                  name="file"
                  type="file"
                  accept=".zip"
                  className="input py-2"
                  required
                  disabled={isUploading}
                />
                <p className="text-xs text-gray-400 mt-1">Max 15GB. Must contain orthophoto and GeoJSONs.</p>
              </div>

              {/* Progress Bar */}
              {isUploading && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-navy-700 font-medium flex items-center gap-2">
                      <Loader size={14} className="animate-spin text-orange-500" />
                      Uploading...
                    </span>
                    <span className="text-orange-600 font-bold">{uploadProgress}%</span>
                  </div>
                  <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
                    <div
                      className="bg-gradient-to-r from-orange-400 to-orange-500 h-3 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                  <p className="text-xs text-gray-400">
                    {uploadProgress < 100 ? 'Uploading to server...' : 'Processing, please wait...'}
                  </p>
                </div>
              )}

              <div>
                <label className="label">Vegetation Indices</label>
                <div className="grid grid-cols-2 gap-3">
                  {['ndvi', 'ndre', 'gndvi', 'savi'].map(idx => (
                    <label key={idx} className="flex items-center p-3 rounded-lg border border-gray-100 hover:border-orange-200 cursor-pointer">
                      <input type="checkbox" name="indices" value={idx} className="mr-3 accent-orange-500" />
                      <span className="text-sm font-medium text-navy-700 uppercase">{idx}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex items-center p-3 rounded-lg bg-orange-50 border border-orange-100">
                <input type="checkbox" name="retain_raw" id="retain_raw" className="mr-3 accent-orange-500" />
                <label htmlFor="retain_raw" className="text-sm text-navy-700 cursor-pointer">
                  <span className="font-medium">Retain raw Float32 data</span>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Enables zonal statistics, Excel exports, and temporal comparison. Uses ~60% more disk space.
                  </p>
                </label>
              </div>

              <div className="flex gap-3 pt-2">
                <button 
                  type="button" 
                  onClick={() => { setShowUpload(false); setUploadError(''); setUploadProgress(0); }} 
                  className="btn-outline flex-1"
                  disabled={isUploading}
                >
                  {isUploading ? 'Uploading...' : 'Cancel'}
                </button>
                <button 
                  type="submit" 
                  className="btn-primary flex-1 disabled:opacity-50"
                  disabled={isUploading}
                >
                  {isUploading ? (
                    <span className="flex items-center justify-center gap-2">
                      <Loader size={14} className="animate-spin" />
                      Uploading...
                    </span>
                  ) : (
                    'Upload & Process'
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
